import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../../../App.css";
import Loader from "../../ui/Loader";
import Table from "../../ui/Table";
import Navbar from "../../ui/Navbar";
import Modal from "../../ui/Modal";
import { swalError, swalSuccess } from "../../../utils/swalAlert";
import { Card } from "../../ui/Card";
import { fetchWithAuth } from "../../../utils/api";
import Report from "../Instructor/pageUI/Results/Report";
import { useReactToPrint } from "react-to-print";
import PerformanceReport from "../../ui/PerformanceReport";
import { AuthContext } from "../../../context/AuthContext";

export default function UPSCResults() {
  const { isAuthenticated, primaryRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const serverUrl = process.env.REACT_APP_SERVER_URL;

  const [scoreData, setScoreData] = useState(null);
  const [classId, setClassId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("table");

  const [answerPaperURL, setAnswerPaperURL] = useState(null);
  const [testPaperURL, setTestPaperURL] = useState(null);
  const [answerPaperComments, setAnswerPaperComments] = useState("");

  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isSummaryGenerating, setIsSummaryGenerating] = useState(false);

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoData, setInfoData] = useState({ source: "", reasoning: "" });

  const pathSegments = window.location.pathname.split("/");
  const answerPaperId = pathSegments[pathSegments.length - 1];

  const [classInfo, setClassInfo] = useState({
    id: null,
    name: "Loading...",
    instructorName: "",
  });
  const [testInfo, setTestInfo] = useState({ id: null, name: "Loading..." });
  const [studentInfo, setStudentInfo] = useState({
    id: null,
    referenceId: "",
    name: "Unknown Student",
  });

  const reportRef = useRef(null);
  const summaryRef = useRef(null);

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated || primaryRole !== "instructor") {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, primaryRole, navigate]);

  const handlePrintReport = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Score Report - ${testInfo.name} - ${studentInfo.name}`,
    pageStyle: `
      @page { size: A4 landscape; margin: 5mm; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    `,
  });

  const handlePrintSummary = useReactToPrint({
    contentRef: summaryRef,
    documentTitle: `Performance Report - ${testInfo.name} - ${studentInfo.name}`,
    pageStyle: `
      @page { size: A4 landscape; margin: 10mm; align: center; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    `,
  });

  const handleOpenInfo = (question) => {
    setInfoData({
      source: question.source || "",
      reasoning: question.reasoning || "",
    });
    setIsInfoModalOpen(true);
  };

  // ── Fetch Results ──
  const fetchResults = useCallback(async () => {
    if (!answerPaperId) {
      setError("Invalid URL: No answer paper ID found");
      setLoading(false);
      return;
    }
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/score/get/${answerPaperId}`,
        { credentials: "include" }
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch results");
      }
      setScoreData(data.score);
    } catch (err) {
      setError(err.message || "Failed to fetch results");
      swalError(err.message || "Failed to fetch results");
    } finally {
      setLoading(false);
    }
  }, [answerPaperId, serverUrl]);

  useEffect(() => {
    fetchResults();

    fetchWithAuth(
      `${serverUrl}/api/breadcrumbs/get?answerPaperId=${answerPaperId}`,
      { credentials: "include" }
    )
      .then((res) => {
        if (!res) return;
        return res.json();
      })
      .then((data) => {
        if (data?.class?.id) {
          setClassInfo({
            name: data.class.name,
            id: data.class.id,
            instructorName: data.class.instructorName,
          });
        }
        if (data?.test?.id) {
          setTestInfo({ name: data.test.name, id: data.test.id });
        }
      })
      .catch(() => {
        setClassInfo({ id: null, name: "Unknown Class", instructorName: "" });
      });
  }, [answerPaperId, serverUrl, fetchResults]);

  // ── Fetch Student Info ──
  useEffect(() => {
    const fetchAnswerPaperAndStudent = async () => {
      try {
        const res = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/get/${answerPaperId}`,
          { credentials: "include" }
        );
        if (!res) return;
        const data = await res.json();
        if (!res.ok) return;

        if (data.studentId) {
          const studentRes = await fetchWithAuth(
            `${serverUrl}/api/user/get/${data.studentId}`,
            { credentials: "include" }
          );
          if (!studentRes) return;
          const studentData = await studentRes.json();
          if (studentRes.ok) {
            const fullName = `${studentData.firstName || ""} ${
              studentData.lastName || ""
            }`;
            setStudentInfo({
              id: studentData._id || null,
              referenceId: studentData.referenceId || "",
              name: fullName || "Unknown Student",
            });
          }
        }
      } catch {
        setStudentInfo({ id: null, referenceId: "", name: "Unknown Student" });
      }
    };
    fetchAnswerPaperAndStudent();
  }, [answerPaperId, serverUrl]);

  // ── Fetch Answer Paper & Test Paper URLs ──
  useEffect(() => {
    const fetchAnswerPaperURL = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/answer-paper/get/${answerPaperId}`,
          { credentials: "include" }
        );
        if (!response) return;
        const data = await response.json();
        setAnswerPaperURL(data.url);
        setAnswerPaperComments(data.comments || "");
      } catch {
        // Silent
      }
    };

    const fetchTestPaperURL = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/test/get/${testInfo.id}`,
          { credentials: "include" }
        );
        if (!response) return;
        const data = await response.json();
        setClassId(data.classId);
        setTestPaperURL(data.testPaperURL);
      } catch {
        // Silent
      }
    };

    fetchAnswerPaperURL();
    if (testInfo.id) {
      fetchTestPaperURL();
    }
  }, [answerPaperId, serverUrl, testInfo.id]);

  // ── Handlers ──
  const handleOpenAnswerPaper = () => {
    if (!answerPaperURL) {
      swalError("Answer paper file not available.");
      return;
    }
    window.open(answerPaperURL, "_blank");
  };

  const handleOpenTestPaper = () => {
    if (!testPaperURL) {
      swalError("Test paper file not available.");
      return;
    }
    window.open(testPaperURL, "_blank");
  };

  const handleViewSummary = () => {
    setIsSummaryModalOpen(true);
    if (!scoreData.summary) {
      setIsSummaryGenerating(true);
      setTimeout(() => setIsSummaryGenerating(false), 800);
    }
  };

  // ── CSV Export ──
  const downloadCSV = () => {
    const header = [
      "Question Number",
      "Question Text",
      "Student's Answer",
      "Obtained Score",
      "Max Score",
      "Feedback",
    ];
    const rows = scoreData.result.map((q) => [
      q.questionNumber,
      `"${(q.questionText || "").replace(/"/g, '""')}"`,
      `"${(q.studentAnswer || "").replace(/"/g, '""')}"`,
      q.obtainedScore,
      q.maxScore,
      `"${(q.feedback || "").replace(/"/g, '""')}"`,
    ]);
    let csvContent = header.join(",") + "\r\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\r\n";
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Score Report - ${testInfo.name} - ${studentInfo.name}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    swalSuccess("CSV file downloaded successfully");
  };

  if (!isAuthenticated || primaryRole !== "instructor") {
    return null;
  }

  const breadcrumbs = [
    { name: "UPSC Portal", path: "/upsc", id: "home" },
    {
      name: `${testInfo.name}`,
      path: `/upsc/results/${answerPaperId}`,
      isLast: true,
      id: "results",
    },
  ];

  if (loading) {
    return (
      <div className="results-page-wrapper">
        <Navbar links={[]} breadcrumbs={breadcrumbs} />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          <Loader style={{ fontSize: "50px", position: "static", left: "auto", top: "auto" }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-page-wrapper">
        <Navbar links={[]} breadcrumbs={breadcrumbs} />
        <div
          className="error"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  if (!scoreData) {
    return (
      <div className="results-page-wrapper">
        <Navbar links={[]} breadcrumbs={breadcrumbs} />
        <div
          className="no-data"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 80px)",
          }}
        >
          No results found
        </div>
      </div>
    );
  }

  const calculateTotalScore = () => {
    const totalObtained = scoreData.result.reduce(
      (sum, q) => sum + q.obtainedScore,
      0
    );
    const totalMax = scoreData.result.reduce((sum, q) => sum + q.maxScore, 0);
    return { obtained: totalObtained, max: totalMax };
  };

  // ── Read-only columns (no edit, no delete) ──
  const columns = [
    { key: "questionNumber", label: "Question Number", sortable: true },
    { key: "questionText", label: "Question Text", sortable: false },
    { key: "studentAnswer", label: "Student's Answer", sortable: false },
    { key: "obtainedScore", label: "Obtained Score", sortable: true },
    { key: "maxScore", label: "Max Score", sortable: true },
    { key: "feedback", label: "Feedback", sortable: false },
    {
      key: "info",
      label: "",
      render: (item) => (
        <button
          className="delete-btn"
          onClick={() => handleOpenInfo(item)}
          title="View Info"
        >
          <i
            className="fa-solid fa-circle-info"
            style={{ fontSize: "1.2rem" }}
          ></i>
        </button>
      ),
      sortable: false,
    },
  ];

  return (
    <>
      <Navbar links={[]} breadcrumbs={breadcrumbs} />

      {/* Hidden report for print */}
      <div style={{ display: "none" }}>
        <Report
          ref={reportRef}
          testName={testInfo.name}
          studentId={studentInfo.referenceId}
          studentName={studentInfo.name}
          resultData={scoreData.result}
        />
      </div>

      <motion.div
        className="submitted-assessment"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
      >
        <motion.div className="upper">
          <motion.div
            className="stats-container"
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div className="stats-box">
              {[
                {
                  title: "View Paper",
                  onClick: handleOpenAnswerPaper,
                  icon: "/imgs/eye_icon.svg",
                },
                {
                  title: "Total Score",
                  value: `${calculateTotalScore().obtained}/${
                    calculateTotalScore().max
                  } (${(
                    (calculateTotalScore().obtained /
                      calculateTotalScore().max) *
                    100
                  ).toFixed(1)}%)`,
                },
                ...(testPaperURL
                  ? [
                      {
                        title: "View Question Paper",
                        onClick: handleOpenTestPaper,
                        icon: "/imgs/eye_icon.svg",
                      },
                    ]
                  : []),
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="stat-card"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  onClick={stat.onClick}
                >
                  <h3>{stat.title}</h3>
                  <p>{stat.value}</p>
                  {stat.icon && (
                    <img
                      src={stat.icon}
                      alt="icon"
                      className="stat-img"
                      width={50}
                    />
                  )}
                  <div className="card-blur"></div>
                </motion.div>
              ))}
            </motion.div>
            <motion.div className="stats-box">
              {[
                {
                  title: "Performance Report",
                  onClick: handleViewSummary,
                  icon: "/imgs/eye_icon.svg",
                },
                {
                  title: "Export as CSV",
                  onClick: downloadCSV,
                  icon: "/imgs/download_icon.svg",
                },
                {
                  title: "Print Scores Report",
                  onClick: handlePrintReport,
                  icon: "/imgs/print_icon.svg",
                },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="stat-card"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  onClick={stat.onClick}
                >
                  <h3>{stat.title}</h3>
                  <p>{stat.value}</p>
                  {stat.icon && (
                    <img
                      src={stat.icon}
                      alt="icon"
                      className="stat-img"
                      width={50}
                    />
                  )}
                  <div className="card-blur"></div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* AI Comments — Read Only */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="card-actions">
              <Card
                title="AI Scoring Comments"
                description={answerPaperComments || "No comments available."}
              >
                <p></p>
              </Card>
            </div>
          </motion.div>
        </motion.div>

        {/* Tab Bar — no manual review toggle */}
        <div className="controls-container">
          <div className="filters-group"></div>
          <div className="tab-bar">
            <button
              className={`tab-button ${activeTab === "table" ? "active" : ""}`}
              onClick={() => setActiveTab("table")}
              style={{
                backgroundColor: activeTab === "table" ? "#4a90e2" : "#f5f5f5",
                color: activeTab === "table" ? "white" : "#333",
                transform: activeTab === "table" ? "scale(1.05)" : "scale(1)",
                transition: "all 0.3s ease",
              }}
            >
              <i className="fa-solid fa-table"></i>
            </button>
            <button
              className={`tab-button ${activeTab === "normal" ? "active" : ""}`}
              onClick={() => setActiveTab("normal")}
              style={{
                backgroundColor:
                  activeTab === "normal" ? "#4a90e2" : "#f5f5f5",
                color: activeTab === "normal" ? "white" : "#333",
                transform:
                  activeTab === "normal" ? "scale(1.05)" : "scale(1)",
                transition: "all 0.3s ease",
              }}
            >
              <i className="fa-solid fa-list"></i>
            </button>
          </div>
        </div>

        {/* Card View */}
        {activeTab === "normal" && (
          <div className="questions-container">
            {scoreData.result.map((question, index) => (
              <motion.div
                key={question._id}
                className="question-card"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="question-header">
                  <h2>{question.questionNumber}</h2>
                  <div className="score-badge">
                    {question.obtainedScore}/{question.maxScore}
                  </div>
                  {/* No edit button */}
                </div>
                <div className="question-content">
                  <p className="question-text">{question.questionText}</p>
                  <div className="answer-section">
                    <div className="student-answer">
                      <label>Student's Answer:</label>
                      <p>{question.studentAnswer}</p>
                    </div>
                  </div>
                  <div className="feedback-section">
                    <label>Feedback:</label>
                    <p>{question.feedback}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Table View */}
        {activeTab === "table" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Table
              data={scoreData.result}
              columns={columns}
              caption={"Assessment Results for " + studentInfo.name}
            />
          </motion.div>
        )}
      </motion.div>

      {/* ── Performance Report Modal (Read-Only — no edit/delete) ── */}
      <Modal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        maxWidth={"100%"}
      >
        {isSummaryGenerating ? (
          <div className="loading-container">
            <p style={{ marginLeft: "48%", marginBottom: "10px" }}>
              Loading ...
            </p>
            <Loader style={{ fontSize: "50px" }} />
          </div>
        ) : (
          <>
            {scoreData.summary ? (
              <div className="summary-content">
                <div ref={summaryRef}>
                  <PerformanceReport
                    summary={{
                      studentName: studentInfo.name,
                      testName: testInfo.name,
                      classId: classId,
                      instructorName: classInfo.instructorName,
                      overallPerformance:
                        scoreData?.summary?.overallPerformance || "",
                      strengths: scoreData?.summary?.strengths || [],
                      weaknesses: scoreData?.summary?.weaknesses || [],
                      recommendations:
                        scoreData?.summary?.recommendations || [],
                      createdAt: scoreData?.summary?.createdAt || "",
                      updatedAt: scoreData?.summary?.updatedAt || "",
                    }}
                    isSummaryEditing={false}
                    onSaveSummary={false}
                    handleSaveSummary={() => {}}
                  />
                </div>
                <div className="modal-actions">
                  {/* Only Print — no edit/delete */}
                  <button
                    className="btn-submit"
                    onClick={handlePrintSummary}
                    title="Print the performance report"
                  >
                    <i className="fa-solid fa-print"></i> Print
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="no-summary-message"
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  backgroundColor: "#fff1f2",
                  borderRadius: "12px",
                  border: "2px dashed #fda4af",
                }}
              >
                <div
                  style={{
                    fontSize: "48px",
                    marginBottom: "16px",
                    color: "#e11d48",
                  }}
                >
                  <i className="fa-solid fa-circle-exclamation"></i>
                </div>
                <h3
                  style={{
                    fontSize: "20px",
                    color: "#9f1239",
                    marginBottom: "8px",
                  }}
                >
                  Performance Report Not Available
                </h3>
                <p
                  style={{
                    color: "#be123c",
                    fontSize: "16px",
                    lineHeight: "1.5",
                    maxWidth: "400px",
                    margin: "0 auto",
                  }}
                >
                  The Performance Report has not been generated for this test
                  yet. Please check back later.
                </p>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ── Info Modal ── */}
      <Modal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)}>
        <h2>Question Insights</h2>
        <div className="form-group">
          <label>Source</label>
          <p>{infoData.source || "Not available"}</p>
        </div>
        <div className="form-group">
          <label>Reasoning</label>
          <p>{infoData.reasoning || "Not available"}</p>
        </div>
      </Modal>
    </>
  );
}
